import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { directAuth } from '../../../../services/directAuth';
import { graphqlApi } from '../../../../services/graphql';

export interface LoginInfoSectionProps {
  email: string;
  onChangePassword: () => void;
  userId?: string;
  oauthProvider?: string;
  kakaoLinked?: boolean;
  naverLinked?: boolean;
  googleLinked?: boolean;
  onChanged?: () => void;
}

/** Simple-login (간편로그인) providers shown in the 로그인정보 section. */
const SOCIAL_PROVIDERS = [
  { key: 'kakao', label: 'Kakao', mark: 'K', bg: '#FEE500', fg: '#3C1E1E', connectable: true },
  { key: 'naver', label: 'Naver', mark: 'N', bg: '#03C75A', fg: '#FFFFFF', connectable: true },
  { key: 'google', label: 'Google', mark: 'G', bg: '#FFFFFF', fg: '#4285F4', connectable: true },
] as const;

/**
 * 로그인정보 section — MatchGo "Bright" styling. Includes 간편로그인 with
 * connect/disconnect for Kakao & Naver. (Google is Cognito-federated and not
 * connectable here yet.)
 */
const LoginInfoSection: React.FC<LoginInfoSectionProps> = ({
  email,
  onChangePassword,
  userId,
  oauthProvider,
  kakaoLinked = false,
  naverLinked = false,
  googleLinked = false,
  onChanged,
}) => {
  const { t } = useTranslation('profile');
  const [busy, setBusy] = useState<string | null>(null);

  const linkedMap: Record<string, boolean> = {
    kakao: kakaoLinked,
    naver: naverLinked,
    google: googleLinked,
  };

  const handleConnect = (provider: 'kakao' | 'naver' | 'google') => {
    try {
      setBusy(provider);
      // Redirects away to the provider; returns to /my-profile/info.
      directAuth.initiateConnectFlow(provider);
    } catch (err: any) {
      setBusy(null);
      toast.error(err?.message || t('myProfile.form.socialConnectFailed', '연결을 시작하지 못했습니다.'));
    }
  };

  const handleDisconnect = async (provider: 'kakao' | 'naver' | 'google') => {
    if (!userId) return;
    if (!window.confirm(t('myProfile.form.socialDisconnectConfirm', '간편로그인 연결을 해제하시겠습니까?'))) return;
    setBusy(provider);
    try {
      const res = await graphqlApi.unlinkSocialAccount(userId, provider);
      if (res.success) {
        toast.success(res.message || t('myProfile.form.socialDisconnected', '연결이 해제되었습니다.'));
        onChanged?.();
      } else {
        toast.error(res.error || t('myProfile.form.socialDisconnectFailed', '연결 해제에 실패했습니다.'));
      }
    } catch (err: any) {
      toast.error(err?.message || t('myProfile.form.socialDisconnectFailed', '연결 해제에 실패했습니다.'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="px-6 sm:px-8 py-7 border-t border-[#EEF1F6]">
      <div className="flex items-center justify-between gap-2 pb-3.5 mb-2 border-b-2 border-[#0F1E3D]">
        <h2 className="text-[18px] font-bold tracking-tight text-[#0F1E3D]">
          {t('myProfile.form.loginInfo')}
        </h2>
        <span className="font-mono text-[10px] font-semibold text-[#6B7794] bg-[#F7F8FB] border border-[#E4E8F0] rounded px-2 py-0.5">
          {t('myProfile.form.instantTag', '즉시 반영')}
        </span>
      </div>

      {/* Password */}
      <div className="grid sm:grid-cols-[120px_1fr] gap-2 sm:gap-4 py-4 border-b border-[#EEF1F6]">
        <div className="text-[13px] font-bold text-[#0F1E3D] sm:pt-2">
          {t('myProfile.form.password', '비밀번호')}
        </div>
        <div>
          <button
            type="button"
            onClick={onChangePassword}
            className="inline-flex items-center justify-center px-4 py-2.5 text-[13.5px] font-bold rounded-lg border-[1.5px] border-[#0F1E3D] text-[#0F1E3D] bg-white hover:bg-[#0F1E3D] hover:text-white transition"
          >
            {t('myProfile.form.changePassword')}
          </button>
        </div>
      </div>

      {/* Email (read-only) */}
      <div className="grid sm:grid-cols-[120px_1fr] gap-2 sm:gap-4 py-4 border-b border-[#EEF1F6]">
        <div className="text-[13px] font-bold text-[#0F1E3D] sm:pt-2.5">
          {t('myProfile.form.email')}
        </div>
        <div>
          <input
            id="email"
            type="email"
            value={email}
            readOnly
            className="w-full max-w-[320px] px-3.5 py-2.5 text-sm rounded-lg border-[1.5px] border-[#E4E8F0] bg-[#F7F8FB] text-[#6B7794] cursor-not-allowed focus:outline-none"
          />
          <p className="mt-2 text-xs text-[#6B7794] leading-relaxed">
            {t('myProfile.form.emailHelp', '가입 시 입력한 값 또는 SNS 연동으로 추출된 메일 주소가 표기됩니다.')}
          </p>
        </div>
      </div>

      {/* 간편로그인 */}
      <div className="grid sm:grid-cols-[120px_1fr] gap-2 sm:gap-4 py-4">
        <div className="text-[13px] font-bold text-[#0F1E3D] sm:pt-2.5">
          {t('myProfile.form.socialLogin', '간편로그인')}
        </div>
        <div className="flex flex-col gap-2.5 max-w-[480px]">
          {SOCIAL_PROVIDERS.map((p) => {
            const linked = !!linkedMap[p.key];
            const isPrimary = (oauthProvider || '').toLowerCase() === p.key;
            const isBusy = busy === p.key;
            // Mirror the backend lockout guard: the primary login can only be
            // disconnected once another provider is linked to fall back on.
            const otherLinkedCount = SOCIAL_PROVIDERS.filter(
              (o) => o.key !== p.key && o.connectable && !!linkedMap[o.key],
            ).length;
            const lockedPrimary = linked && isPrimary && otherLinkedCount === 0;
            return (
              <div
                key={p.key}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl border-[1.5px] ${
                  linked ? 'border-[#2D5BFF] bg-[#E6EDFF]' : 'border-[#E4E8F0] bg-white'
                }`}
              >
                <span
                  className="w-[26px] h-[26px] rounded-[7px] flex items-center justify-center font-bold text-[13px] flex-shrink-0"
                  style={{ background: p.bg, color: p.fg, border: p.key === 'google' ? '1px solid #E4E8F0' : 'none' }}
                  aria-hidden="true"
                >
                  {p.mark}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-[13.5px] font-bold text-[#0F1E3D]">
                    {t('myProfile.form.socialLoginName', '{{provider}} 로그인 연결', { provider: p.label })}
                  </div>
                  {linked && isPrimary && (
                    <div className="text-[11px] text-[#6B7794] mt-0.5">{t('myProfile.form.socialPrimary', '주 로그인')}</div>
                  )}
                </div>

                {/* Action — toggle switch (on = connected) */}
                {!p.connectable ? (
                  <button
                    type="button"
                    role="switch"
                    aria-checked={false}
                    aria-label={t('myProfile.form.socialLoginName', '{{provider}} 로그인 연결', { provider: p.label })}
                    disabled
                    title={t('myProfile.form.socialComingSoon', '준비 중')}
                    className="relative inline-flex h-6 w-[42px] flex-shrink-0 rounded-full bg-[#E4E8F0] opacity-50 cursor-not-allowed"
                  >
                    <span className="absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow" />
                  </button>
                ) : (
                  <button
                    type="button"
                    role="switch"
                    aria-checked={linked}
                    aria-label={t('myProfile.form.socialLoginName', '{{provider}} 로그인 연결', { provider: p.label })}
                    disabled={isBusy || lockedPrimary}
                    title={
                      lockedPrimary
                        ? t('myProfile.form.socialPrimaryLock', '주 로그인 수단은 해제할 수 없습니다. 다른 로그인 수단을 먼저 연결해 주세요.')
                        : undefined
                    }
                    onClick={() =>
                      linked
                        ? handleDisconnect(p.key as 'kakao' | 'naver' | 'google')
                        : handleConnect(p.key as 'kakao' | 'naver' | 'google')
                    }
                    className={`relative inline-flex h-6 w-[42px] flex-shrink-0 rounded-full transition-colors ${
                      linked ? 'bg-[#2D5BFF]' : 'bg-[#E4E8F0]'
                    } ${isBusy || lockedPrimary ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <span
                      className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${
                        linked ? 'left-[20px]' : 'left-0.5'
                      }`}
                    />
                  </button>
                )}
              </div>
            );
          })}
          <p className="text-[11.5px] text-[#6B7794] leading-relaxed mt-1">
            {t('myProfile.form.socialLoginNote2', '연결하면 해당 소셜 계정으로도 로그인할 수 있습니다.')}
          </p>
        </div>
      </div>
    </section>
  );
};

export default LoginInfoSection;
